import React, { useEffect, useCallback, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Simple full-screen lightbox.
 * Click on backdrop / X closes; click on the image itself does NOT close.
 */
export default function Lightbox({ images, index, onClose, onNavigate }) {
  const open = index !== null && index !== undefined && index >= 0;
  const total = images.length;
  const [touchStart, setTouchStart] = useState(null);

  const next = useCallback(() => {
    if (!open) return;
    onNavigate((index + 1) % total);
  }, [index, total, onNavigate, open]);

  const prev = useCallback(() => {
    if (!open) return;
    onNavigate((index - 1 + total) % total);
  }, [index, total, onNavigate, open]);

  // keyboard
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, next, prev, onClose]);

  if (!open) return null;

  const current = images[index];
  const stop = (e) => e.stopPropagation();

  // touch swipe
  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart === null) return;
    const delta = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(delta) > 50) {
      if (delta < 0) next();
      else prev();
    }
    setTouchStart(null);
  };

  return (
    <div
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={current?.alt || "Photo viewer"}
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="fixed inset-0 z-[60] bg-[#15130F]/95 backdrop-blur-sm flex items-center justify-center"
    >
      {/* Top bar — counter + close. Buttons handle their own onClick; bubbling to root is fine. */}
      <div className="pointer-events-none absolute top-0 inset-x-0 flex items-center justify-between px-6 md:px-10 py-5 text-[#F5F2EC]/90">
        <span className="pointer-events-auto text-[11px] uppercase tracking-[0.3em]" onClick={stop}>
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
        <button
          data-testid="lightbox-close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close"
          className="pointer-events-auto p-2 hover:text-[#B85532] transition-colors"
        >
          <X className="w-6 h-6" strokeWidth={1.4} />
        </button>
      </div>

      {/* Image — only the <img> stops propagation so clicks AROUND it close the modal. */}
      <img
        key={current.src}
        src={current.src}
        alt={current.alt}
        onClick={stop}
        className="lightbox-img-fade max-h-[85vh] max-w-[92vw] md:max-w-[85vw] object-contain shadow-2xl"
      />

      {/* Caption */}
      {current.alt && (
        <div
          onClick={stop}
          className="pointer-events-none absolute bottom-0 inset-x-0 text-center px-8 pb-6 pt-10 bg-gradient-to-t from-black/60 to-transparent text-[#F5F2EC]"
        >
          <p className="font-serif italic text-base md:text-lg">{current.alt}</p>
        </div>
      )}

      {/* Prev / next */}
      <button
        data-testid="lightbox-prev"
        onClick={(e) => {
          e.stopPropagation();
          prev();
        }}
        aria-label="Previous photo"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 p-3 md:p-4 text-[#F5F2EC]/80 hover:text-[#B85532] hover:scale-110 transition-all"
      >
        <ChevronLeft className="w-7 h-7 md:w-8 md:h-8" strokeWidth={1.2} />
      </button>
      <button
        data-testid="lightbox-next"
        onClick={(e) => {
          e.stopPropagation();
          next();
        }}
        aria-label="Next photo"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 p-3 md:p-4 text-[#F5F2EC]/80 hover:text-[#B85532] hover:scale-110 transition-all"
      >
        <ChevronRight className="w-7 h-7 md:w-8 md:h-8" strokeWidth={1.2} />
      </button>
    </div>
  );
}
